import { isRadiantThemeEnabled } from '@thoughtspot/session-service';
import { hexToOklch, oklchToHex } from './oklch-palette';
/**
 * Returns true when Radiant dark mode is active:
 *  - radiantThemeEnabled session flag must be on
 *  - data-theme="dark" must be set on <html>
 */
export const isDarkModeActive = () => {
    if (!isRadiantThemeEnabled())
        return false;
    return (typeof document !== 'undefined' &&
        document.documentElement.getAttribute('data-theme') === 'dark');
};
// ---------------------------------------------------------------------------
// OKLCH lightness remap
// ---------------------------------------------------------------------------
//
// OKLCH is the cylindrical form of the OKLab perceptual color space:
//   L = perceptual lightness (0..1), C = chroma, H = hue (radians here).
// Remapping lightness in OKLab keeps perceived brightness uniform across hues
// (unlike HSL, where the same L looks brighter for yellow than for blue), so
// palette entries land at a consistent, readable brightness on a dark surface
// while hue and chroma are preserved for inter-slice distinctiveness.
//
// The conversions live in oklch-palette.ts (shared with the runtime theme
// generator). This remap keeps the plain per-channel-clamp oklchToHex — its
// output is pinned by dark-mode-color-utils.spec.ts and must not change.
/**
 * Picks the target OKLCH lightness for a data-viz color on a dark surface.
 * Band-based remap in perceptual (OKLab) lightness 0..1:
 *  - Very dark colors (L < 0.30): lift to 0.78 — big, vibrant lift
 *  - Mid-range colors (L < 0.65): lift to 0.80
 *  - Light colors     (L >= 0.65): 0.82 — minimal adjustment
 */
function oklchDarkModeLightness(L) {
    if (L < 0.3)
        return 0.78;
    if (L < 0.65)
        return 0.8;
    return 0.82;
}
/**
 * Shifts each color to a dark-mode-friendly value by remapping its OKLCH
 * lightness into a dark-mode band while preserving chroma and hue, so palette
 * entries stay vivid and distinct on a dark background. Handles "#RRGGBB" hex
 * strings and "rgba(r,g,b,a)" strings (produced by convertToAlpha for bubble
 * and scatter charts); invalid values pass through unchanged.
 */
export const generateDarkModePalette = (lightColors) => lightColors.map((colorStr) => {
    try {
        if (colorStr.startsWith('#')) {
            const [L, C, H] = hexToOklch(colorStr);
            return oklchToHex(oklchDarkModeLightness(L), C, H);
        }
        // Handle rgba()/rgb() strings produced by convertToAlpha for
        // bubble and scatter charts. A single bounded capture group keeps
        // the pattern free of nested quantifiers (no ReDoS risk); the
        // comma-split below validates the channel count.
        const rgbMatch = colorStr.match(/^rgba?\(([^)]+)\)$/);
        const parts = rgbMatch ? rgbMatch[1].split(',') : [];
        if (parts.length === 3 || parts.length === 4) {
            const r = parseInt(parts[0], 10);
            const g = parseInt(parts[1], 10);
            const b = parseInt(parts[2], 10);
            const alpha = parts[3] !== undefined ? parseFloat(parts[3]) : 1;
            const toByte = (x) => x.toString(16).padStart(2, '0');
            const hex = `#${toByte(r)}${toByte(g)}${toByte(b)}`;
            const [L, C, H] = hexToOklch(hex);
            const darkHex = oklchToHex(oklchDarkModeLightness(L), C, H);
            const dr = parseInt(darkHex.slice(1, 3), 16);
            const dg = parseInt(darkHex.slice(3, 5), 16);
            const db = parseInt(darkHex.slice(5, 7), 16);
            return `rgba(${dr}, ${dg}, ${db}, ${alpha})`;
        }
        return colorStr;
    }
    catch (_a) {
        // Invalid/unsupported color (shorthand hex, out-of-range rgb,
        // etc.) — pass through unchanged rather than emit "#NaNNaNNaN".
        return colorStr;
    }
});
/**
 * Watches the data-theme attribute on <html> and calls callback whenever it
 * changes. Returns a cleanup function.
 *
 * Used by chart renderers to reactively update series colors when the user
 * toggles between light and dark mode while a chart is already on screen, and
 * to handle the timing gap between chart mount and the useEffect in
 * authenticated-app-layout that sets data-theme.
 */
export const subscribeToThemeChanges = (callback) => {
    if (typeof document === 'undefined') {
        return () => undefined;
    }
    const observer = new MutationObserver(callback);
    observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme'],
    });
    return () => {
        observer.disconnect();
    };
};
/*
 * Helpers for resolving radiant design-system colors into the literal hex
 * values that third-party canvases (e.g. Monaco's `defineTheme`) require.
 * Shared here so every app (blink-v2, data-model, …) resolves dark-mode
 * editor colors the same way instead of copy-pasting these helpers per editor.
 */
/**
 * Normalize a CSS color to a 6-digit hex. Passes through `#rrggbb`, converts
 * `rgb()`/`rgba()`, and falls back for anything else (e.g. `oklch()`) so
 * consumers never silently break if the radiant token output format changes.
 */
export const toHex = (color, fallback) => {
    if (/^#[0-9a-fA-F]{6}$/.test(color))
        return color;
    const m = color.match(/^rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
    if (m) {
        return `#${[m[1], m[2], m[3]]
            .map(n => Number(n)
            .toString(16)
            .padStart(2, '0'))
            .join('')}`;
    }
    return fallback;
};
/**
 * Resolve a radiant CSS custom property to a literal hex value. Reads the live
 * computed value off `<html>` — it tracks the active light/dark/brand theme.
 * Falls back when the var is unset or non-hex (tests / non-radiant surfaces).
 */
export const getTokenColor = (cssVar, fallback) => {
    if (typeof document === 'undefined')
        return fallback;
    const value = getComputedStyle(document.documentElement)
        .getPropertyValue(cssVar)
        .trim();
    return toHex(value, fallback);
};
/**
 * Like {@link getTokenColor} but returns bare hex with no leading `#` — the
 * form Monaco's `rules[].foreground` expects.
 */
export const tokenRuleColor = (cssVar, fallback) => getTokenColor(cssVar, fallback).replace('#', '');
/** True when the app is in dark mode (`data-theme="dark"` on `<html>`). */
export const isDarkTheme = () => typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-theme') === 'dark';
//# sourceMappingURL=dark-mode-color-utils.js.map